<template>
  <div class="flex flex-col gap-5 text-left" data-testid="bulk-sector-specialty-fields">
    <p
      v-if="disabledReason"
      class="rounded bg-gray-50 px-3 py-2 text-sm text-gray-600"
      data-testid="bulk-disabled-reason"
    >
      {{ disabledReason }}
    </p>
    <template v-else>
      <section v-for="field in fields" :key="field.key" :data-testid="`bulk-${field.key}-field`">
        <div class="flex flex-wrap items-center gap-2">
          <span class="font-bold text-gray-800">{{ field.title }}</span>
          <span
            v-for="chip in field.current"
            :key="chip"
            class="rounded-full bg-gray-100 px-2 text-xs text-gray-600"
            :data-testid="`bulk-${field.key}-current`"
          >
            {{ chip }}
          </span>
        </div>
        <div
          class="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3"
          role="radiogroup"
          :aria-label="field.title"
        >
          <button
            v-for="option in field.options"
            :key="option.mode"
            type="button"
            role="radio"
            :aria-checked="field.state.mode === option.mode"
            class="rounded border px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
            :class="[
              field.state.mode === option.mode ? 'border-primary30 bg-primary5' : 'border-gray-300',
              { 'border-solid': isMobileLayout }
            ]"
            :disabled="option.disabled"
            :data-testid="`bulk-${field.key}-mode-${option.mode}`"
            @click="field.setMode(option.mode)"
          >
            <b>{{ option.label }}</b>
            <br />
            <small class="text-gray-500">{{ option.hint }}</small>
          </button>
        </div>
        <div v-if="field.state.mode === 'set'" class="mt-2">
          <SectorCombobox
            v-if="field.key === 'sector'"
            :model-value="sector.value"
            :health-unit-id="unitIds.length === 1 ? unitIds[0] : null"
            test-id="bulk-sector-combobox"
            @update:model-value="emit('update:sector', { mode: 'set', value: $event })"
          />
          <SpecialtyCombobox
            v-else
            :model-value="specialty.value"
            :company-id="companyIds.length === 1 ? companyIds[0] : null"
            :official-only="companyIds.length !== 1"
            test-id="bulk-specialty-combobox"
            @update:model-value="emit('update:specialty', { mode: 'set', value: $event })"
          />
        </div>
        <p class="mt-2 text-sm text-gray-700" :data-testid="`bulk-${field.key}-preview`">
          {{ field.preview }}
        </p>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import SectorCombobox from './SectorCombobox.vue'
import SpecialtyCombobox from './SpecialtyCombobox.vue'
import {
  currentValueSummary,
  describeFieldPreview,
  previewSector,
  previewSpecialty,
  type BulkFieldState,
  type BulkItem
} from '@/modules/company/Dashboard/lenses/bulkPreview'
import type { BulkFieldMode } from '@/Network/Types/Requests/StationRequests'
import type { StationSectorRef } from '@/Network/Types/Responses/StationResponses'
import type { SpecialtySelection } from '@/utils/sectorSpecialty'
import { useIsMobile } from '@/hooks/useIsMobile'

/**
 * Bloco "Setor e especialidade" de um lote (H7/H8-b): Manter / Definir / Limpar por campo,
 * com a prévia local. Controlado (`v-model:sector` / `v-model:specialty`); nunca sugere
 * valor (P2) — "Definir" começa vazio até o usuário escolher.
 */
const props = withDefaults(
  defineProps<{
    items: BulkItem[]
    sector: BulkFieldState<StationSectorRef>
    specialty: BulkFieldState<SpecialtySelection>
    /** Ex.: "Indisponível: 2 unidades" (H7) ou "Indisponível ao mover" (H8). Trava só o "Definir" do setor. */
    sectorLockedReason?: string | null
    /** Bloqueia o bloco inteiro (ex.: "Selecionar todos" na Consulta). */
    disabledReason?: string | null
  }>(),
  { sectorLockedReason: null, disabledReason: null }
)

const emit = defineEmits<{
  'update:sector': [value: BulkFieldState<StationSectorRef>]
  'update:specialty': [value: BulkFieldState<SpecialtySelection>]
}>()

// Celular (QA-01): o reset do Vuetify zera `border-style` em <button>; o desktop fica como está.
const { isMobileLayout } = useIsMobile()

const unitIds = computed(() => [
  ...new Set(props.items.map((item) => item.healthUnitId).filter((id): id is number => id !== null))
])
const companyIds = computed(() => [
  ...new Set(props.items.map((item) => item.companyId).filter((id): id is number => id !== null))
])

watch(
  () => props.sectorLockedReason,
  (reason) => {
    if (reason && props.sector.mode === 'set') emit('update:sector', { mode: 'keep', value: null })
  },
  { immediate: true }
)

const fields = computed(() => [
  {
    key: 'sector' as const,
    title: 'Setor',
    state: props.sector,
    current: currentValueSummary(props.items, 'setor'),
    preview: describeFieldPreview(
      previewSector(props.items, props.sector),
      props.sector.mode,
      !!props.sector.value,
      'setor'
    ),
    setMode: (mode: BulkFieldMode) =>
      emit('update:sector', { mode, value: mode === 'set' ? props.sector.value : null }),
    options: [
      {
        mode: 'keep' as const,
        label: 'Manter como está',
        hint: 'Nenhum plantão muda',
        disabled: false
      },
      {
        mode: 'set' as const,
        label: 'Definir valor',
        hint: props.sectorLockedReason ?? 'Mesmo valor em todos',
        disabled: !!props.sectorLockedReason
      },
      { mode: 'clear' as const, label: 'Limpar', hint: 'Deixa o campo vazio', disabled: false }
    ]
  },
  {
    key: 'specialty' as const,
    title: 'Especialidade',
    state: props.specialty,
    current: currentValueSummary(props.items, 'especialidade'),
    preview: describeFieldPreview(
      previewSpecialty(props.items, props.specialty),
      props.specialty.mode,
      !!props.specialty.value,
      'especialidade'
    ),
    setMode: (mode: BulkFieldMode) =>
      emit('update:specialty', { mode, value: mode === 'set' ? props.specialty.value : null }),
    options: [
      {
        mode: 'keep' as const,
        label: 'Manter como está',
        hint: 'Nenhum plantão muda',
        disabled: false
      },
      {
        mode: 'set' as const,
        label: 'Definir valor',
        hint:
          companyIds.value.length > 1
            ? 'Só do catálogo oficial (várias empresas)'
            : 'Mesmo valor em todos',
        disabled: false
      },
      { mode: 'clear' as const, label: 'Limpar', hint: 'Deixa o campo vazio', disabled: false }
    ]
  }
])
</script>
